/**
 * TEMPORARY workaround — delete once the component-library FilterChip emits a
 * structured value (follow-up: SERV-2013).
 *
 * The onengine FilterChip's date menu only reports a formatted display label via
 * `onItemsChange` (e.g. `"06/01/2026"` or `"06/01/2026 – 06/18/2026"`); it never
 * surfaces the underlying Date/ISO values. The work-order list API, however,
 * expects a `"<startISO>|<endISO>"` range (see `handleDateRangeFilter` in
 * work-orders.queries.ts). So we parse the chip's label back into ISO here.
 *
 * This couples us to the library's `en-US` `MM/DD/YYYY` label format — that is
 * the cost of the workaround and the reason it is temporary.
 */

const RANGE_SEPARATOR = " – "; // en dash + spaces, matches the chip's label builder

const parseMmDdYyyy = (raw: string, endOfDay: boolean): Date | null => {
  const [mm, dd, yyyy] = raw.trim().split("/").map(Number);
  if (!mm || !dd || !yyyy) return null;
  return endOfDay
    ? new Date(yyyy, mm - 1, dd, 23, 59, 59, 999)
    : new Date(yyyy, mm - 1, dd, 0, 0, 0, 0);
};

/**
 * Decodes a chip date label into the `"<startISO>|<endISO>"` range the list API
 * consumes. A single date expands to that whole day. Returns null for an
 * unparseable label so the caller can drop the entry instead of sending garbage.
 */
export const decodeDateRangeLabel = (label: string): string | null => {
  const datePart = label.split(",")[0]; // drop any trailing time portion
  const [startRaw, endRaw] = datePart.split(RANGE_SEPARATOR);
  const start = parseMmDdYyyy(startRaw, false);
  const end = parseMmDdYyyy(endRaw ?? startRaw, true);
  if (!start || !end) return null;
  return `${start.toISOString()}|${end.toISOString()}`;
};

const parseNumericPart = (part: string): number | null => {
  const cleaned = part.replace(/[^\d.]/g, ""); // strip currency symbol, separators, spaces
  if (!cleaned) return null;
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
};

/**
 * TEMPORARY workaround (no follow-up ticket yet — same root cause as the date
 * chip in SERV-2013, but that ticket is scoped to the date/datetime menu only;
 * the numerical chip needs its own structured-value fix) — the FilterChip
 * numerical menu reports only a currency-formatted label via `onItemsChange`:
 *   both bounds:  `"$5.00 – $15.00"`  (en dash)
 *   min only:     `"$5.00+"`
 *   max only:     `"≤ $15.00"`
 * The work-order list API's `handleNteRangeFilter` expects a JSON `{ min, max }`
 * string (it `JSON.parse`s `filterValue.id`). We decode the label back to that
 * shape here. Returns null for an unparseable label so the caller can drop it.
 */
export const decodeNumericRangeLabel = (label: string): string | null => {
  const trimmed = label.trim();
  let min: number | null = null;
  let max: number | null = null;

  if (trimmed.startsWith("≤")) {
    max = parseNumericPart(trimmed);
  } else if (trimmed.endsWith("+")) {
    min = parseNumericPart(trimmed);
  } else {
    const [minRaw, maxRaw] = trimmed.split(RANGE_SEPARATOR);
    min = parseNumericPart(minRaw ?? "");
    max = maxRaw != null ? parseNumericPart(maxRaw) : null;
  }

  if (min == null && max == null) return null;
  return JSON.stringify({ min, max });
};
